"use client";

// ImportSteps — "bring your memory in", built on StepShell
// (THE sheet, rising from the bottom edge, one height for its whole
// life) with the same progress dots, left-aligned body and skippable navigation.
//
// Three ways in, offered as one quiet list (the house's .list rows, the real
// marks on the right), each a couple of stepped panes — and, for a person moving
// in is open to (F.moveIn, MOVING-IN-PLAN.md §3–7), two more, first:
//   • your ai, over innernet — one paste (PROMPT_MOVE_IN) and the connected ai
//     moves what it knows of you in through innernet_import; the sheet waits for
//     the run and fills as it lands.
//   • files and notes — drop any text files, a folder or a zip of notes
//     (AGENTS.md, CLAUDE.md, rules files, a notes export): each is read here and
//     shown with what kind of thing it is before anything is sent (/api/intake).
// With moving in open every lane is sorted where it lands (facts, projects,
// identity lines) and ends on "here's what moved in" (components/intake/MovedIn).
//   • from ChatGPT or Claude — the export guide, then drop the .zip (parsed here
//     by jszip, lib/self/parseExport). POSTs { sources }.
//   • an innernet archive — the zip from settings → data (lib/export). Read here
//     (lib/export/readArchive): what it holds is said before anything is sent,
//     the maps come back through /api/account/restore (the same save every map
//     write goes through), the facts through the import, and writing over a map
//     you still hold asks twice in the one action slot (law_open_dont_unfold).
//   • ask your ai — copy the import prompt, paste what it writes back. POSTs { text }.
// The facts lanes hit /api/self/import-web → the same server-side importer.
// Dropping an innernet archive into the export lane moves to the archive lane
// on its own. Every step has one action, bottom right; "back" and "not now" are
// quiet words. Used standalone (settings · /me · connections) and handed over
// to from the connect window (ConnectGuideModal), which leaves first.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import BrandIcon from "../BrandIcon";
import PromptWindow from "../PromptWindow";
import StepShell, { type FlowStep } from "./StepShell";
import { useSheetLeave } from "../../lib/os/useSheetLeave";
import { trackFunnel, EVENTS } from "../../lib/analytics";
import { PROMPT_IMPORT, PROMPT_IMPORT_SERIES, PROMPT_MOVE_IN, PROMPT_MOVE_IN_DEEP, IMPORT_SOURCES } from "../../lib/onboarding/prompts";
import { nextKind, readPicked, sends, TOOL_WORDS, type PickedFile } from "../../lib/intake/readFiles";
import { KIND_WORDS } from "../../lib/intake/sources";
import { MovedInView, WrittenList, useMovedIn, runMeta } from "../intake/MovedIn";
import { ReviewBody, useReview } from "../intake/Review";
import { useResumable } from "../../lib/os/resume";
import { MOVED_CHANGED_EVENT, movedChanged } from "../../lib/intake/open";
import { kindOf } from "../../lib/intake/sources";
import type { IntakeRun } from "../../lib/intake/runs";
import { parseExportFile, type ParsedExport } from "../../lib/self/parseExport";
import { OVER_CAP_LINE, overPasteCap, postImport, type ImportResult } from "../../lib/self/importResponse";
import { readArchiveZip, restorePieces, sourceBatches, type ReadArchive } from "../../lib/export/readArchive";

const EXPORT_GUIDES: Record<string, { name: string; icon: string; steps: string[] }> = {
  chatgpt: {
    name: "ChatGPT",
    icon: "openai",
    steps: [
      "open ChatGPT → Settings → Data controls",
      "click Export data, then confirm",
      "open the email from OpenAI and download the .zip",
    ],
  },
  claude: {
    name: "Claude",
    icon: "claude",
    steps: [
      "open Claude → Settings → Privacy",
      "click Export data",
      "open the email from Anthropic and download the .zip",
    ],
  },
};

type Lane = "prompt" | "upload" | "archive" | "files" | "ai" | null;

/** a send that did not get through, tried again on its own before anything is said (MOVING-IN-PLAN §16) */
const SEND_RETRIES = [2_000, 6_000, 15_000, 30_000];

/** is moving in open to this person, and their latest move-ins (the runs route answers 404 when it is not) */
async function moveInProbe(): Promise<{ open: boolean; runs: IntakeRun[]; staged: IntakeRun[] }> {
  try {
    const r = await fetch("/api/intake/runs", { cache: "no-store" });
    if (!r.ok) return { open: false, runs: [], staged: [] };
    const j = (await r.json().catch(() => ({}))) as { runs?: IntakeRun[]; staged?: IntakeRun[] };
    return { open: true, runs: j.runs ?? [], staged: j.staged ?? (j.runs ?? []).filter((x) => x.stage === "staged") };
  } catch {
    return { open: false, runs: [], staged: [] };
  }
}

/** what an innernet archive brought back */
interface ArchiveOutcome {
  maps: number;
  pages: number;
  facts: number;
  questions: number;
  /** the facts were sent and read (even when nothing new came of them) */
  factsRead: boolean;
  /** a line for anything that did not come back, said plainly */
  missed: string[];
}

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;
const day = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }).toLowerCase();
};

/** a zip the parser could not open speaks for itself only when it says something a person can use */
function fileLine(err: unknown): string {
  const said = err instanceof Error ? err.message : "";
  return said && !/central directory|end of data|corrupt|unexpected|invalid|not a valid|offset/i.test(said)
    ? said
    : "that file couldn't be opened. please choose the .zip you downloaded, as it came.";
}

export default function ImportSteps({
  onClose,
  onExit,
  openedFrom = "unknown",
}: {
  onClose: () => void;
  onExit?: () => void;
  /** which surface offered the import (see ImportFlow). Carried on the import
   *  events so the same flow's conversion can be read per moment. */
  openedFrom?: string;
}) {
  const router = useRouter();
  // every way out plays the way down first (the sheet sinks, then the caller unmounts it)
  const { open, leave } = useSheetLeave(onClose);
  const [step, setStep] = useState(0);
  const [lane, setLane] = useState<Lane>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // what is still going, said without alarm: a send tried again, a read netti finishes on its own (§16)
  const [note, setNote] = useState("");

  // prompt lane
  const [text, setText] = useState("");
  const [source, setSource] = useState("chatgpt");
  const [series, setSeries] = useState(false);

  // export lane
  const [exp, setExp] = useState<"chatgpt" | "claude">("chatgpt");
  const [parsed, setParsed] = useState<ParsedExport | null>(null);

  // archive lane
  const [archive, setArchive] = useState<ReadArchive | null>(null);
  const [existing, setExisting] = useState<string[]>([]);
  const [bringMaps, setBringMaps] = useState(true);
  const [bringFacts, setBringFacts] = useState(true);
  const [confirmOver, setConfirmOver] = useState(false);
  const [progress, setProgress] = useState("");
  const [outcome, setOutcome] = useState<ArchiveOutcome | null>(null);

  // the one file field, shared by both drop lanes
  const [fileName, setFileName] = useState("");
  const [parsing, setParsing] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // moving in (F.moveIn): the two lanes it adds, and the run every sorted lane ends on
  const [moveIn, setMoveIn] = useState(false);
  const [picked, setPicked] = useState<PickedFile[]>([]);
  const [skipped, setSkipped] = useState<string[]>([]);
  const [runId, setRunId] = useState<string | null>(null);
  const [aiSince, setAiSince] = useState<number | null>(null);
  // the ai may move in through the doors every connection has (the prompt's fallback), which opens no run to watch
  const [aiWaited, setAiWaited] = useState(false);
  // the deep walk by default: everything the ai can reach, part by part; the quick one is a press away
  const [quick, setQuick] = useState(false);
  // the files lane's run across sends, so a retry after a failure sends only what is left, under the same run
  const sentRun = useRef<string | null>(null);
  const filesRef = useRef<HTMLInputElement>(null);
  const folderRef = useRef<HTMLInputElement>(null);
  const moved = useMovedIn(runId);
  // a staged move-in waits for the person's look (lib/intake/stage): its run is kept where the sheet can find it
  // again (lib/os/resume), and the server's list says when one is waiting from another visit or device
  const [stagedRun, setStagedRun, forgetStaged] = useResumable<string | null>("import-sheet:staged", null);
  const [waiting, setWaiting] = useState<IntakeRun | null>(null);
  const review = useReview(stagedRun);
  const [lettingGo, setLettingGo] = useState(false);
  useEffect(() => {
    let alive = true;
    // asked again whenever a move-in is staged, kept, let go or taken back, here or anywhere
    const probe = () =>
      void moveInProbe().then(({ open, staged }) => {
        if (!alive) return;
        setMoveIn(open);
        setWaiting(staged[0] ?? null);
      });
    probe();
    window.addEventListener(MOVED_CHANGED_EVENT, probe);
    return () => {
      alive = false;
      window.removeEventListener(MOVED_CHANGED_EVENT, probe);
    };
  }, []);
  // a review that was decided elsewhere (or let go) is not this sheet's any more
  useEffect(() => {
    if (stagedRun && (review.plan === "decided" || review.plan === "missing")) forgetStaged();
  }, [stagedRun, review.plan, forgetStaged]);

  const pickLane = (l: Lane) => {
    // each lane starts clean: a run another lane found is never this one's
    setLane(l); setError(""); setNote(""); setStep(1); setRunId(null); setResult(null); setAiWaited(false);
    sentRun.current = null;
    if (l === "ai") setAiSince(Date.now());
  };

  // your ai, over innernet: the sheet watches for the run the ai opens (a run of the ai lane, begun after this lane was
  // opened), then the view fills as it lands (useMovedIn polls while the run is still open)
  useEffect(() => {
    if (lane !== "ai" || runId || aiSince == null) return;
    let alive = true;
    const look = async () => {
      try {
        const r = await fetch("/api/intake/runs", { cache: "no-store" });
        const j = (await r.json()) as { runs?: Array<{ id: string; lane: string; started_at: string }> };
        const hit = (j.runs ?? []).find((x) => x.lane === "ai" && Date.parse(x.started_at) >= aiSince - 60_000);
        if (alive && hit) setRunId(hit.id);
      } catch {
        /* asked again on the next beat */
      }
    };
    void look();
    const t = setInterval(look, 5000);
    const quiet = setTimeout(() => alive && setAiWaited(true), 90_000);
    return () => { alive = false; clearInterval(t); clearTimeout(quiet); };
  }, [lane, runId, aiSince]);

  /** An innernet archive, wherever it was dropped: read it, ask which maps are already here. */
  const takeArchive = useCallback(async (a: ReadArchive) => {
    setArchive(a);
    setParsed(null);
    setConfirmOver(false);
    setBringMaps(a.maps.length > 0);
    setBringFacts(a.facts.length > 0);
    setExisting([]);
    if (lane !== "archive") { setLane("archive"); setStep(1); }
    if (a.maps.length) {
      try {
        const res = await fetch("/api/account/restore", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ check: a.maps.map((m) => m.slug) }),
        });
        const j = (await res.json().catch(() => ({}))) as { existing?: string[] };
        if (res.ok) setExisting(j.existing ?? []);
      } catch {
        /* the restore asks again by saving: a map already here is updated, never doubled */
      }
    }
  }, [lane]);

  const handleFile = useCallback(async (file: File | null) => {
    if (!file) return;
    setError(""); setParsed(null); setArchive(null); setFileName(file.name); setParsing(true);
    try {
      const isZip = !file.name.toLowerCase().endsWith(".json");
      const a = isZip ? await readArchiveZip(await file.arrayBuffer()).catch(() => null) : null;
      if (a) {
        await takeArchive(a);
      } else if (lane === "archive") {
        throw new Error("this isn't an innernet archive. it is the .zip from settings → data → export my data.");
      } else {
        const p = await parseExportFile(file);
        setParsed(p);
        // an export dropped anywhere is named by what it is (a claude export in the files lane is claude's)
        if (p.kind === "chatgpt" || p.kind === "claude") setExp(p.kind);
      }
    } catch (err) {
      setError(fileLine(err));
      // the file never opened — a wall people hit before the import can start,
      // and invisible in an import_started/completed pair. Only its shape ships.
      trackFunnel(EVENTS.error_shown, { reason: "export_parse_failed", method: lane === "archive" ? "archive" : "upload", opened_from: openedFrom });
      setFileName("");
    } finally { setParsing(false); }
  }, [lane, takeArchive, openedFrom]);

  const done = (data: ImportResult) => { setResult(data); if (data.run) setRunId(data.run); router.refresh(); };

  // files and notes: read here, every file shown with its kind before anything is sent
  const handlePicked = useCallback(async (list: FileList | null) => {
    if (!list || !list.length) return;
    setError(""); setParsing(true);
    const got = await readPicked(Array.from(list));
    setParsing(false);
    if (got.archiveZip) { void handleFile(got.archiveZip); return; }
    if (got.exportZip) {
      setLane("upload"); setStep(2); void handleFile(got.exportZip); return;
    }
    setPicked((was) => {
      const seen = new Set(was.map((f) => f.id));
      return [...was, ...got.files.filter((f) => !seen.has(f.id))];
    });
    setSkipped(got.skipped);
    if (!got.files.length && got.skipped.length) setError("none of those could be read as text. please choose notes, markdown or text files.");
  }, [handleFile]);

  /**
   * Moving in, looked at first (lib/intake/stage): send the sources to be sorted, writing nothing, in sends
   * of at most 50 files and 150k characters under one run; then the sheet shows the review. True when
   * every send went in.
   */
  const stage = async (sources: Array<{ title: string; kind?: string; for?: string; text: string; dated?: string | null }>, lane: string, label: string) => {
    const parts = sends(sources.map((s) => ({ title: s.title, kind: (s.kind ?? kindOf(s.title, s.text).kind) as never, text: s.text, ...(s.for != null ? { for: s.for as never } : {}), ...(s.dated ? { dated: s.dated } : {}) })));
    let run: string | null = sentRun.current;
    setNote("");
    for (const [i, part] of parts.entries()) {
      // one part, sent until it is in: what the server could not reach in one call is sent again (it skips what
      // it already holds), and a blip is tried again on its own; only a refusal the person must see is said
      let again = 0;
      for (let tries = 0; ; ) {
        setProgress(parts.length > 1 ? `netti is reading · ${i + 1} of ${parts.length}` : "netti is reading");
        let res: Response | null = null;
        let j: { run?: string | null; error?: string; rest?: number; again?: boolean; kept?: boolean; full?: boolean; items?: number } = {};
        try {
          res = await fetch("/api/intake/stage", {
            method: "POST",
            headers: { "content-type": "application/json" },
            // whole: the pile is this one send, so the server may know it again by its hashes
            body: JSON.stringify({ sources: part, run, lane, source: label, ...(parts.length === 1 ? { whole: true } : {}) }),
          });
          j = (await res.json().catch(() => ({}))) as typeof j;
        } catch {
          res = null;
        }
        if (res?.ok) {
          run = j.run ?? run;
          sentRun.current = run;
          setNote("");
          // this part was not taken (the look is full, or it could not be saved): say so, and open what already waits
          if (j.kept === false) {
            if (j.full) setError(j.error ?? "that is more than one look can hold. please keep what is waiting first, then bring in the rest.");
            else setNote(j.error ?? "that part goes in when you bring it in again.");
            setProgress("");
            sentRun.current = null;
            if (run && (j.items ?? 0) > 0) { setStagedRun(run); movedChanged(run); }
            return { ok: false, sent: i };
          }
          if (j.again && run) {
            // this pile is already sorted and waiting: its plan is shown again
            setProgress("");
            sentRun.current = null;
            setStagedRun(run);
            movedChanged(run);
            return { ok: true, sent: parts.length };
          }
          if (j.rest && again < 8) {
            again++;
            continue;
          }
          break;
        }
        // a refusal only the person can answer (too much at once, nothing readable, signed out): said plainly
        if (res && res.status >= 400 && res.status < 500 && res.status !== 408 && res.status !== 429) {
          setError(j.error ?? "that didn't go through. please try again.");
          setProgress("");
          if (run && i > 0) movedChanged(run);
          return { ok: false, sent: i };
        }
        if (tries >= SEND_RETRIES.length) {
          // still not through after a while: quiet, and what is read stays read (the server skips it next time)
          setNote("innernet could not be reached just now. what netti read is kept; it goes on when you bring it in again.");
          setProgress("");
          if (run) movedChanged(run);
          return { ok: false, sent: i };
        }
        setNote(tries ? "still reading. innernet is slow just now." : "");
        await new Promise((ok) => setTimeout(ok, SEND_RETRIES[tries]));
        tries++;
      }
    }
    setProgress("");
    setNote("");
    sentRun.current = null;
    if (run) { setStagedRun(run); movedChanged(run); }
    return { ok: !!run, sent: parts.length };
  };

  const submitFiles = async () => {
    if (!picked.length) return;
    if (moveIn) {
      setLoading(true); setError("");
      trackFunnel(EVENTS.import_started, { method: "files", source: "files", opened_from: openedFrom, files: picked.length, staged: true });
      await stage(picked.map(({ title, kind, text, ...rest }) => ({ title, kind, text, ...(rest.for != null ? { for: rest.for } : {}) })), "files", "your files");
      setLoading(false);
      return;
    }
    setLoading(true); setError("");
    trackFunnel(EVENTS.import_started, { method: "files", source: "files", opened_from: openedFrom, files: picked.length });
    // each send carries whole files (only one longer than a send goes in parts), so a file is either in or still here to send again
    const parts = sends(picked.map(({ title, kind, text, ...rest }) => ({ title, kind, text, ...(rest.for != null ? { for: rest.for } : {}) })));
    let run: string | null = sentRun.current;
    let failed = false;
    const sent = new Set<string>();
    for (const [i, part] of parts.entries()) {
      setProgress(parts.length > 1 ? `netti is reading · ${i + 1} of ${parts.length}` : "netti is reading");
      try {
        const res = await fetch("/api/intake", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ sources: part, run, done: i === parts.length - 1, lane: "files", source: "your files" }),
        });
        const j = (await res.json().catch(() => ({}))) as { run?: string | null; error?: string; left?: number };
        if (!res.ok) { setError(j.error ?? "that didn't go through. please try again."); failed = true; break; }
        run = j.run ?? run;
        sentRun.current = run;
        for (const p of part) sent.add(p.title.replace(/ \(part \d+ of \d+\)$/, ""));
        if (j.left) { setError("some of it is still to be read. please press bring it in again for the rest."); failed = true; break; }
      } catch {
        setError("the connection dropped. what was sent is in; please press bring it in again for the rest.");
        failed = true;
        break;
      }
    }
    setProgress("");
    setLoading(false);
    if (failed) {
      // what went in stays in; only what did not is left here to send again, under the same run
      setPicked((was) => was.filter((f) => !sent.has(f.title)));
      return;
    }
    sentRun.current = null;
    if (run) {
      setRunId(run);
      router.refresh();
      trackFunnel(EVENTS.import_completed, { method: "files", source: "files", opened_from: openedFrom, files: picked.length });
    } else {
      setError("it is in, but the record of what moved in could not be kept. your memory has it all.");
    }
  };

  // the paste lane's ceiling is the server's (200,000 characters). Said before
  // anything is sent, here and in the line under the box.
  const overCap = overPasteCap(text);

  const submitPaste = async () => {
    if (!text.trim()) {
      setError("paste what your assistant wrote first");
      trackFunnel(EVENTS.error_shown, { reason: "empty_paste", method: "paste", opened_from: openedFrom });
      return;
    }
    if (overCap) {
      setError(OVER_CAP_LINE);
      trackFunnel(EVENTS.error_shown, { reason: "paste_over_cap", method: "paste", opened_from: openedFrom });
      return;
    }
    setLoading(true); setError("");
    // counts and enum-ish ids only — the pasted memory itself never leaves this function.
    trackFunnel(EVENTS.import_started, { method: "paste", source, opened_from: openedFrom, ...(moveIn ? { staged: true } : {}) });
    const sourceLabel = IMPORT_SOURCES.find((s) => s.id === source)?.label ?? source;
    if (moveIn) {
      await stage([{ title: `what ${sourceLabel} wrote`, text: text.trim() }], "paste", sourceLabel);
      setLoading(false);
      return;
    }
    const out = await postImport({ text: text.trim(), source: sourceLabel });
    if (out.ok) {
      done(out.data);
      trackFunnel(EVENTS.import_completed, {
        method: "paste", source, opened_from: openedFrom,
        facts_written: out.data.facts_written,
        questions_written: out.data.questions_written,
      });
    } else {
      setError(out.line);
      trackFunnel(EVENTS.error_shown, { reason: "import_failed", cause: out.cause, method: "paste", source, opened_from: openedFrom });
    }
    setLoading(false);
  };

  const submitUpload = async () => {
    if (!parsed || parsed.sources.length === 0) return;
    setLoading(true); setError("");
    trackFunnel(EVENTS.import_started, { method: "upload", source: exp, opened_from: openedFrom, conversations: parsed.kept, ...(moveIn ? { staged: true } : {}) });
    if (moveIn) {
      await stage(parsed.sources.map((s) => ({ title: s.label, kind: "chat", text: s.content, dated: s.dated ?? null })), "export", EXPORT_GUIDES[exp].name);
      setLoading(false);
      return;
    }
    const out = await postImport({ sources: parsed.sources, source: EXPORT_GUIDES[exp].name });
    if (out.ok) {
      done(out.data);
      trackFunnel(EVENTS.import_completed, {
        method: "upload", source: exp, opened_from: openedFrom,
        conversations: parsed.kept,
        facts_written: out.data.facts_written,
        questions_written: out.data.questions_written,
      });
    } else {
      setError(out.line);
      trackFunnel(EVENTS.error_shown, { reason: "import_failed", cause: out.cause, method: "upload", source: exp, opened_from: openedFrom });
    }
    setLoading(false);
  };

  const overwriting = bringMaps && archive ? archive.maps.filter((m) => existing.includes(m.slug)) : [];

  const submitArchive = async () => {
    if (!archive || (!bringMaps && !bringFacts)) return;
    // writing over a map you still hold is the reaching act: it asks once more, in the same slot
    if (overwriting.length && !confirmOver) { setConfirmOver(true); return; }
    setLoading(true); setError("");
    trackFunnel(EVENTS.import_started, { method: "archive", source: "innernet", opened_from: openedFrom, maps: bringMaps ? archive.maps.length : 0 });
    const got: ArchiveOutcome = { maps: 0, pages: 0, facts: 0, questions: 0, factsRead: false, missed: [] };

    if (bringMaps) {
      for (const [i, m] of archive.maps.entries()) {
        setProgress(`bringing back ${m.name} · ${i + 1} of ${archive.maps.length}`);
        let ok = true;
        for (const piece of restorePieces(m.body)) {
          try {
            const res = await fetch("/api/account/restore", {
              method: "POST",
              headers: { "content-type": "application/json" },
              body: JSON.stringify({ project: piece }),
            });
            if (!res.ok) {
              const j = (await res.json().catch(() => ({}))) as { error?: string };
              got.missed.push(`${m.name}: ${j.error ?? "it did not save just now"}`);
              ok = false;
              break;
            }
          } catch {
            got.missed.push(`${m.name}: the connection dropped`);
            ok = false;
            break;
          }
        }
        if (ok) { got.maps += 1; got.pages += m.pages; }
      }
    }

    if (bringFacts && archive.facts.length) {
      const batches = sourceBatches(archive.facts);
      for (const [i, batch] of batches.entries()) {
        setProgress(`netti is reading your facts again${batches.length > 1 ? ` · ${i + 1} of ${batches.length}` : ""}`);
        const out = await postImport({ sources: batch, source: "innernet archive" });
        if (!out.ok) { got.missed.push(`your facts: ${out.line}`); break; }
        got.factsRead = true;
        got.facts += out.data.facts_written;
        got.questions += out.data.questions_written;
      }
    }

    setProgress("");
    setLoading(false);
    setOutcome(got);
    router.refresh();
    trackFunnel(EVENTS.import_completed, {
      method: "archive", source: "innernet", opened_from: openedFrom,
      maps: got.maps, facts_written: got.facts, questions_written: got.questions, missed: got.missed.length,
    });
  };

  // the one drop field, worn by both file lanes
  const dropField = (idle: { name: string; sub: string }, ready: React.ReactNode) => (
    <>
      <button
        type="button"
        className="im-drop"
        data-drag={drag || undefined}
        data-ready={ready ? "true" : undefined}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); void handleFile(e.dataTransfer.files?.[0] ?? null); }}
      >
        {parsing ? (
          <span className="im-drop-sub">reading {fileName}…</span>
        ) : ready ? (
          <>
            <span className="im-drop-name">{fileName}</span>
            {ready}
            <span className="im-drop-sub">choose a different file</span>
          </>
        ) : (
          <>
            <span className="im-drop-name">{idle.name}</span>
            <span className="im-drop-sub">{idle.sub}</span>
          </>
        )}
      </button>
      <input ref={inputRef} type="file" accept=".zip,.json,application/zip,application/json" style={{ display: "none" }} onChange={(e) => { void handleFile(e.target.files?.[0] ?? null); e.target.value = ""; }} />
      {error && <p className="im-error" role="alert">{error}</p>}
      {note && !error && <p className="im-quiet" role="status">{note}</p>}
    </>
  );

  const steps: FlowStep[] = useMemo(() => {
    if (outcome) {
      const back = [outcome.maps ? plural(outcome.maps, "memory", "memories") : "", outcome.facts ? plural(outcome.facts, "fact") : ""].filter(Boolean);
      return [{
        key: "archive-done",
        title: back.length ? `${back.join(" and ")} back` : outcome.factsRead ? "your facts were read again" : "nothing came back",
        hideDot: true,
        body: (
          <div>
            {outcome.maps > 0 && (
              <p className="cm-sub">{plural(outcome.pages, "page")} across {plural(outcome.maps, "memory", "memories")}, each saved as a commit, so the history stays readable.</p>
            )}
            {outcome.facts > 0 && <p className="cm-sub">netti read your facts again and filed each one. what was in your own words is yours again.</p>}
            {outcome.factsRead && outcome.facts === 0 && <p className="cm-sub">netti read your facts again. they were already here, so nothing new was added.</p>}
            {outcome.questions > 0 && (
              <p className="cm-where"><span aria-hidden>↳</span> {plural(outcome.questions, "open question")} for you to answer when you like.</p>
            )}
            {outcome.missed.map((m) => <p key={m} className="im-error">{m}</p>)}
          </div>
        ),
        primary: { label: "see your memory", onClick: leave, done: true },
      }];
    }
    // moving in, looked at first: every change netti would make, kept, unselected or changed before anything is written
    if (stagedRun && !runId) {
      const ready = typeof review.plan === "object";
      const keeping = review.plan === "keeping";
      return [{
        key: "review",
        title: ready ? "here's what netti would keep" : keeping ? "netti is keeping it" : review.plan === "error" ? "your move-in is waiting" : "netti is reading",
        why: ready
          ? "nothing is written until you say so. unselect or change anything. you can leave and come back to it."
          : keeping
            ? "what you kept is being written into your memory. it takes a moment."
            : review.plan === "error"
              ? "it could not be opened just now. nothing was lost."
              : "it is sorting what you brought, writing nothing yet.",
        hideDot: true,
        body: (
          <>
            <ReviewBody review={review} />
            {error && <p className="im-error" role="alert">{error}</p>}
            {note && !error && <p className="im-quiet" role="status">{note}</p>}
            {ready && (
              <p className="rv-letgo">
                <button
                  type="button"
                  className="quiet-link"
                  onClick={async () => {
                    if (!lettingGo) { setLettingGo(true); return; }
                    if (await review.discard()) { forgetStaged(); setError(""); setLettingGo(false); setLane(null); setStep(0); }
                  }}
                >
                  {lettingGo ? "yes, let all of it go. nothing is written" : "let all of it go"}
                </button>
              </p>
            )}
          </>
        ),
        primary: {
          label: review.busy ? "keeping it…" : ready ? `keep ${review.kept} of ${review.total}` : "keep it",
          onClick: async () => {
            const run = stagedRun;
            if (await review.apply()) {
              forgetStaged();
              setError("");
              setRunId(run);
              router.refresh();
              trackFunnel(EVENTS.import_completed, { method: lane ?? "files", opened_from: openedFrom, kept: review.kept, staged: true });
            }
          },
          disabled: !ready || review.kept === 0 || review.busy,
          loading: review.busy,
        },
        skip: ready || keeping || review.plan === "error" ? { label: "come back to it later", onClick: leave } : undefined,
      }];
    }
    // a sorted lane (moving in) ends on what moved in; the ai lane shows it inside its own step
    if (runId && lane !== "ai") {
      return [{
        key: "moved",
        title: "here's what moved in",
        hideDot: true,
        body: (
          <>
            <MovedInView read={moved} onLeave={leave} />
            {typeof moved === "object" && moved.run.status !== "open" && <WrittenList runId={moved.run.id} />}
          </>
        ),
        primary: { label: "explore", onClick: leave, done: true },
      }];
    }
    if (result) {
      return [{
        key: "done",
        title: `${plural(result.facts_written, "fact")} brought in`,
        hideDot: true,
        body: (
          <div>
            <p className="cm-sub">{result.summary}</p>
            {result.questions_written > 0 && (
              <p className="cm-where"><span aria-hidden>↳</span> {plural(result.questions_written, "open question")} for you to answer when you like.</p>
            )}
          </div>
        ),
        primary: { label: "see your memory", onClick: leave, done: true },
      }];
    }

    const choose: FlowStep = {
      key: "choose",
      title: "where is it now?",
      why: "innernet reads it on our end, so you never paste a key, and nothing is sent until you say so.",
      body: (
        <div className="list im-ways" role="list">
          {moveIn && waiting && !stagedRun && (
            <button type="button" role="listitem" className="list-row im-way im-way--waiting" onClick={() => { setLane("files"); setStagedRun(waiting.id); setStep(1); }}>
              <span className="im-way-text">
                <span className="list-name">you were bringing memory in</span>
                <span className="im-way-sub">it is sorted and waiting for your look, {runMeta(waiting)}. nothing is written until you say so.</span>
              </span>
            </button>
          )}
          {moveIn && (
            <>
              <button type="button" role="listitem" className="list-row im-way" onClick={() => pickLane("ai")}>
                <span className="im-way-text">
                  <span className="list-name">your ai, over innernet</span>
                  <span className="im-way-sub">paste one line where your ai is connected. it moves what it knows of you in, sorted: you, your projects, how it should work.</span>
                </span>
                <span className="im-way-marks" aria-hidden>
                  <BrandIcon name="openai" size={16} title="" />
                  <BrandIcon name="claude" size={16} title="" />
                  <BrandIcon name="gemini" size={16} title="" />
                </span>
              </button>
              <button type="button" role="listitem" className="list-row im-way" onClick={() => pickLane("files")}>
                <span className="im-way-text">
                  <span className="list-name">files and notes</span>
                  <span className="im-way-sub">AGENTS.md, CLAUDE.md, rules files, a notes export, any text. read here first, and you see each one before it goes.</span>
                </span>
                <span className="im-way-marks" aria-hidden>
                  <BrandIcon name="innernet" size={16} title="" />
                </span>
              </button>
            </>
          )}
          <button type="button" role="listitem" className="list-row im-way" onClick={() => pickLane("upload")}>
            <span className="im-way-text">
              <span className="list-name">in ChatGPT or Claude</span>
              <span className="im-way-sub">their data export, a .zip. unpacked here in your browser, and only your conversations are read.</span>
            </span>
            <span className="im-way-marks" aria-hidden>
              <BrandIcon name="openai" size={16} title="" />
              <BrandIcon name="claude" size={16} title="" />
            </span>
          </button>
          <button type="button" role="listitem" className="list-row im-way" onClick={() => pickLane("archive")}>
            <span className="im-way-text">
              <span className="list-name">an innernet archive</span>
              <span className="im-way-sub">the .zip you took out of innernet. your memories come back as they were, and your facts are read again.</span>
            </span>
            <span className="im-way-marks" aria-hidden>
              <BrandIcon name="innernet" size={16} title="" />
            </span>
          </button>
          <button type="button" role="listitem" className="list-row im-way" onClick={() => pickLane("prompt")}>
            <span className="im-way-text">
              <span className="list-name">ask your ai</span>
              <span className="im-way-sub">run one prompt where your ai already knows you, then paste back what it writes.</span>
            </span>
            <span className="im-way-marks" aria-hidden>
              <BrandIcon name="openai" size={16} title="" />
              <BrandIcon name="claude" size={16} title="" />
              <BrandIcon name="gemini" size={16} title="" />
            </span>
          </button>
        </div>
      ),
      // the choice is made in the body: "not now" is a quiet word, not a pill
      primary: { label: "not now", onClick: leave, quiet: true },
    };

    if (lane === "files") {
      const total = picked.reduce((k, f) => k + f.size, 0);
      return [
        choose,
        {
          key: "files-pick",
          title: "choose your files and notes",
          why: "they are read here, on your machine. you see each one and what it is before anything is sent.",
          body: (
            <>
              <button
                type="button"
                className="im-drop"
                data-drag={drag || undefined}
                data-ready={picked.length ? "true" : undefined}
                onClick={() => filesRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                onDragLeave={() => setDrag(false)}
                onDrop={(e) => { e.preventDefault(); setDrag(false); void handlePicked(e.dataTransfer.files ?? null); }}
              >
                {parsing ? (
                  <span className="im-drop-sub">reading…</span>
                ) : (
                  <>
                    <span className="im-drop-name">{picked.length ? `${plural(picked.length, "file")} ready` : "drop files here, or choose them"}</span>
                    <span className="im-drop-sub">{picked.length ? `${total < 1000 ? `${total.toLocaleString("en-US")} characters` : `${Math.round(total / 1000).toLocaleString("en-US")}k characters`} · add more` : "markdown, text, rules files, a zip of notes"}</span>
                  </>
                )}
              </button>
              <input ref={filesRef} type="file" multiple style={{ display: "none" }} onChange={(e) => { void handlePicked(e.target.files); e.target.value = ""; }} />
              <input
                ref={folderRef}
                type="file"
                multiple
                style={{ display: "none" }}
                {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
                onChange={(e) => { void handlePicked(e.target.files); e.target.value = ""; }}
              />
              <button type="button" className="quiet-link im-aside" onClick={() => folderRef.current?.click()}>or choose a whole folder</button>
              <p className="cm-where"><span aria-hidden>↳</span> an innernet-move-in.json an ai made for you goes here too. it is read as it is, with nothing to sort.</p>
              {picked.length > 0 && (
                <div className="detail-rows im-ledger im-files" role="list" aria-label="what will be read">
                  {picked.slice(0, 40).map((f) => (
                    <div key={f.id} className="detail-row" role="listitem">
                      <span className="im-file-name" title={f.id}>{f.title}</span>
                      <span className="im-file-kind">
                        <button
                          type="button"
                          className="quiet-link"
                          aria-label={`${f.title} is read as ${KIND_WORDS[f.kind]}. change it`}
                          onClick={() => setPicked((was) => was.map((x) => (x.id === f.id ? { ...x, kind: nextKind(x.kind), ...(nextKind(x.kind) === "instructions" ? { for: x.for ?? "" } : {}) } : x)))}
                        >
                          {KIND_WORDS[f.kind]}{f.kind === "instructions" ? ` · ${TOOL_WORDS[f.for ?? ""]}` : ""}
                        </button>
                        <button type="button" className="quiet-link im-file-out" aria-label={`leave out ${f.title}`} onClick={() => setPicked((was) => was.filter((x) => x.id !== f.id))}>
                          leave out
                        </button>
                      </span>
                    </div>
                  ))}
                  {picked.length > 40 && <div className="detail-row"><span>and {(picked.length - 40).toLocaleString("en-US")} more</span><span /></div>}
                </div>
              )}
              {skipped.length > 0 && (
                <p className="cm-where"><span aria-hidden>↳</span> {plural(skipped.length, "file")} left out: not text, empty or too large.</p>
              )}
              {progress && <p className="cm-where" aria-live="polite"><span aria-hidden>↳</span> {progress}</p>}
              {error && <p className="im-error" role="alert">{error}</p>}
              {note && !error && <p className="im-quiet" role="status">{note}</p>}
            </>
          ),
          primary: {
            label: loading ? "bringing it in…" : picked.length ? `bring in ${plural(picked.length, "file")}` : "bring it in",
            onClick: submitFiles,
            disabled: !picked.length || parsing,
            loading,
          },
        },
      ];
    }

    if (lane === "ai") {
      return [
        choose,
        {
          key: "ai-run",
          title: runId ? "here's what moved in" : "paste this where your ai is connected",
          why: runId
            ? "it fills as your ai sends it. you can close this; it keeps coming in."
            : "in a chat where innernet is connected (claude, chatgpt, cursor…). it sends what it knows of you, sorted, and this fills as it lands.",
          body: runId ? (
            <MovedInView read={moved} onLeave={leave} />
          ) : (
            <>
              <PromptWindow prompt={quick ? PROMPT_MOVE_IN : PROMPT_MOVE_IN_DEEP} caption={quick ? "the quick move-in" : "the deep move-in"} maxHeight={200} />
              <button type="button" className="quiet-link im-aside" onClick={() => setQuick((q) => !q)}>
                {quick ? "go deep instead: everything it can reach, part by part" : "just the short version instead"}
              </button>
              <p className="cm-where"><span aria-hidden>↳</span> an ai that cannot reach innernet makes you an innernet-move-in.json instead. drop it into files and notes.</p>
              <p className="cm-where" aria-live="polite">
                <span aria-hidden>↳</span>{" "}
                {aiWaited
                  ? "nothing has come through innernet_import yet. if your ai brought it in another way, it is in your memory already."
                  : "waiting for your ai to begin…"}
              </p>
            </>
          ),
          primary: runId
            ? { label: "explore", onClick: leave, done: true }
            : aiWaited
              ? { label: "see your memory", onClick: leave, done: true }
              : { label: "not now", onClick: leave, quiet: true },
        },
      ];
    }

    if (lane === "prompt") {
      return [
        choose,
        {
          key: "prompt-run",
          title: "run this where your ai knows you",
          why: "paste it into a new chat, then copy everything it writes back.",
          body: (
            <>
              {series ? (
                <div className="im-series">
                  {PROMPT_IMPORT_SERIES.map((p, i) => (
                    <PromptWindow key={i} prompt={p} maxHeight={128} caption={`part ${i + 1} of 3`} />
                  ))}
                </div>
              ) : (
                <PromptWindow prompt={PROMPT_IMPORT} caption="the import prompt" maxHeight={168} />
              )}
              <button type="button" className="quiet-link im-aside" onClick={() => setSeries((s) => !s)}>
                {series ? "use the single prompt instead" : "too long for one message? use the 3-part version"}
              </button>
              <div className="im-field-label">which ai did you ask?</div>
              <div className="im-chips">
                {IMPORT_SOURCES.map((s) => (
                  <button key={s.id} type="button" className="chip" data-on={source === s.id || undefined} aria-pressed={source === s.id} onClick={() => setSource(s.id)}>
                    {s.id !== "other" && <BrandIcon name={s.id} size={14} title={s.label} />}
                    {s.label}
                  </button>
                ))}
              </div>
            </>
          ),
          primary: { label: "i have its answer", onClick: () => setStep(2) },
        },
        {
          key: "prompt-paste",
          title: "paste what it wrote back",
          why: "each fact is kept at the most private class that fits it; private ones never leave your own view.",
          body: (
            <>
              <textarea
                className="input-soft im-textarea"
                value={text}
                onChange={(e) => { setText(e.target.value); if (error) setError(""); }}
                placeholder={moveIn ? "paste anything: a note, a memory file, what your ai wrote…" : "paste the facts your assistant wrote…"}
                rows={8}
              />
              {(overCap || error) && <p className="im-error" role="alert">{overCap ? OVER_CAP_LINE : error}</p>}
              {note && !error && !overCap && <p className="im-quiet" role="status">{note}</p>}
            </>
          ),
          primary: { label: loading ? "bringing it in…" : "bring it in", onClick: submitPaste, disabled: !text.trim() || overCap, loading },
        },
      ];
    }

    if (lane === "upload") {
      return [
        choose,
        {
          key: "upload-get",
          title: "download your export",
          why: "it comes by email, usually within a few minutes.",
          body: (
            <>
              <div className="im-chips" role="group" aria-label="which assistant" style={{ marginBottom: "1.2rem" }}>
                {(["chatgpt", "claude"] as const).map((id) => (
                  <button key={id} type="button" className="chip" data-on={exp === id || undefined} aria-pressed={exp === id} onClick={() => { setExp(id); setParsed(null); setFileName(""); setError(""); }}>
                    <BrandIcon name={EXPORT_GUIDES[id].icon} size={14} title={EXPORT_GUIDES[id].name} />
                    {EXPORT_GUIDES[id].name}
                  </button>
                ))}
              </div>
              <ol className="cm-steps">
                {EXPORT_GUIDES[exp].steps.map((st, i) => (
                  <li key={i}><span className="cm-step-n">{i + 1}</span><span className="cm-step-body">{st}</span></li>
                ))}
              </ol>
            </>
          ),
          primary: { label: "i have the .zip", onClick: () => setStep(2) },
        },
        {
          key: "upload-drop",
          title: "drop your export",
          why: "it is unpacked here. only the text of your conversations is sent, and only when you press bring it in.",
          body: dropField(
            { name: "drop the .zip here, or choose it", sub: `the file ${EXPORT_GUIDES[exp].name} emailed you, as it came` },
            parsed ? (
              <span className="im-drop-ok">
                {plural(parsed.kept, "conversation")} ready{parsed.found > parsed.kept ? `, the most recent ${parsed.kept} of ${parsed.found}` : ""}
              </span>
            ) : null,
          ),
          primary: {
            label: loading ? "bringing it in…" : parsed ? `bring in ${plural(parsed.kept, "conversation")}` : "bring it in",
            onClick: submitUpload,
            disabled: !parsed || parsed.sources.length === 0,
            loading,
          },
        },
      ];
    }

    if (lane === "archive") {
      const nothing = !bringMaps && !bringFacts;
      const label = loading
        ? "bringing it back…"
        : confirmOver && overwriting.length
          ? `yes, write over ${plural(overwriting.length, "memory", "memories")}`
          : "bring it back";
      return [
        choose,
        {
          key: "archive-drop",
          title: "drop your archive",
          why: archive
            ? `made ${day(archive.exportedAt)}. choose what comes back.`
            : "the .zip from settings → data → export my data. it is read here, and nothing is sent until you press bring it back.",
          body: (
            <>
              {dropField(
                { name: "drop the archive here, or choose it", sub: "innernet-<date>.zip, as you saved it" },
                archive ? (
                  <span className="im-drop-ok">
                    {[archive.maps.length ? plural(archive.maps.length, "memory", "memories") : "", archive.factCount ? plural(archive.factCount, "fact") : ""].filter(Boolean).join(" · ") || "nothing to bring back in it"}
                  </span>
                ) : null,
              )}
              {archive && (
                <>
                  <div className="im-field-label">what comes back</div>
                  <div className="im-chips" role="group" aria-label="what comes back">
                    <button type="button" className="chip" disabled={!archive.maps.length || loading} data-on={(bringMaps && archive.maps.length > 0) || undefined} aria-pressed={bringMaps} onClick={() => { setBringMaps((v) => !v); setConfirmOver(false); }}>
                      your memories · {archive.maps.length}
                    </button>
                    <button type="button" className="chip" disabled={!archive.facts.length || loading} data-on={(bringFacts && archive.facts.length > 0) || undefined} aria-pressed={bringFacts} onClick={() => setBringFacts((v) => !v)}>
                      your facts · {archive.factCount}
                    </button>
                  </div>
                  {bringMaps && archive.maps.length > 0 && (
                    <div className="detail-rows im-ledger">
                      {archive.maps.slice(0, 6).map((m) => (
                        <div key={m.slug} className="detail-row">
                          <span>{m.name}</span>
                          <span>{existing.includes(m.slug) ? "already here · written over" : plural(m.pages, "page")}</span>
                        </div>
                      ))}
                      {archive.maps.length > 6 && (
                        <div className="detail-row"><span>and {(archive.maps.length - 6).toLocaleString("en-US")} more</span><span /></div>
                      )}
                    </div>
                  )}
                  {overwriting.length > 0 && (
                    <p className="cm-where">
                      <span aria-hidden>↳</span> {plural(overwriting.length, "memory", "memories")} {overwriting.length === 1 ? "is" : "are"} already here. bringing {overwriting.length === 1 ? "it" : "them"} back writes the archived pages over the ones there; every save is a commit, so the newer words stay in the history.
                    </p>
                  )}
                  {progress && <p className="cm-where" aria-live="polite"><span aria-hidden>↳</span> {progress}</p>}
                </>
              )}
            </>
          ),
          primary: { label, onClick: submitArchive, disabled: !archive || nothing || parsing, loading },
        },
      ];
    }

    return [choose];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, outcome, lane, series, source, text, overCap, error, note, loading, exp, parsed, archive, existing, bringMaps, bringFacts, confirmOver, progress, fileName, parsing, drag, leave, moveIn, picked, skipped, runId, moved, aiWaited, stagedRun, review, lettingGo, waiting, quick]);

  return (
    <StepShell
      ariaLabel="bring your memory in"
      head={<><BrandIcon name="innernet" size={26} title="innernet" /><span className="cm-name">bring your memory in</span></>}
      steps={steps}
      step={step}
      setStep={(n) => { setConfirmOver(false); setStep(n); }}
      onClose={leave}
      onExit={step === 0 ? onExit : undefined}
      open={open}
    />
  );
}
